/**
 * The Documents tab — the drawer's record fields (VALE-927).
 *
 * What the drawer knows about the document, as a record rather than as a
 * caption: its type, size and upload date; whether it is a certified copy; and
 * the one line saying what it is.
 *
 * ## It is the attribute treatment, not a bespoke panel
 *
 * These are a record's fields, shown at rest and edited in place, so they render
 * through `AttributeGrid` like every other one (`components/attributes/CLAUDE.md`
 * §0, first row: an existing record, commit-on-blur, no footer). A hand-rolled
 * click-to-edit note was written here first and was simply the antipattern
 * `frontend/CLAUDE.md` names — it re-derived the hover target, the editor ring,
 * the saving indicator and the commit contract, all of which the grid owns and
 * all of which would have drifted from the Contacts drawer the moment either
 * changed.
 *
 * ## Four of the six rows are `readOnly`, and that is a statement
 *
 * Type, size, upload date and the original filename are facts about the stored
 * file, not fields of the record — there is no commit path that could change
 * them, so `readOnly` is the declared way to say "this record does not own this
 * value" (§2). They earn their place because the drawer is where a solicitor
 * checks a document, and "which of these two is the 2.7 MB scan" is a question
 * the list's caption could only answer by being read twice.
 *
 * **The original filename is the one a reader is most likely to want and least
 * likely to have.** Every other surface shows the *display* name — a label the
 * processors derive from the record — so the name a bank or a client actually
 * sent the file under exists nowhere else in the product.
 *
 * ## The note
 *
 * It usually comes from the document analysis, and an analysis is sometimes
 * **wrong about the document** — which matters more here than a wrong field
 * elsewhere, because this is the line a solicitor reads *instead of* opening the
 * file. Two properties follow:
 *
 * - **The draft starts from the sentence, not from nothing.** That is what lets
 *   one field serve two jobs which would otherwise need two: **correcting** the
 *   analysis is editing the text in front of you, and **annotating** is
 *   appending to it. Seeding empty would make every correction a retype, and a
 *   retyped sentence is how you get a note contradicting the one it replaced.
 * - **Clearing is the revert.** `toPatch` sends `''` for an emptied row, the
 *   backend nulls the column, and the row falls back to the analysis's own
 *   sentence. "Delete my note" and "go back to Vale's summary" are deliberately
 *   one operation, so they cannot drift apart — and the fallback resolves to
 *   what the analysis says *now*, not to a stale copy.
 */

'use client';

import { AttributeGrid, type AttributeSpec } from '@/components/attributes';
import type { DocumentItem } from '@/components/documents/v2';
import { getFileType } from '@/lib/utils/data-transformers';
import { formatDateLong } from '@/lib/utils/date-format';
import { formatFileSize } from './labels';

/** Every field this grid can send, in one patch shape. */
interface DocumentAttributePatch {
  notes?: string;
  isCertifiedCopy?: boolean;
}

/**
 * The boolean row's draft/display derivation, matching `flagDraft` in
 * `contact-attribute-specs.ts`. Deliberately re-stated rather than imported: it
 * is private there, and the package's own contract (§3) is that a boolean row's
 * `toPatch` is written by the caller because the key and its side effects vary
 * per field. Two lines of derivation is the shared part, and it is this small.
 */
const flagDraft = (value: boolean) => ({
  display: value ? 'Yes' : 'No',
  draft: value ? 'yes' : 'no'
});

/**
 * `toPatch` for a row that has no commit path.
 *
 * The type requires it on every spec, but a `readOnly` row renders no editor at
 * all (§2), so nothing can ever call this — it is a hole in the type rather than
 * a behaviour. `null` is the right shape regardless: the grid reads it as a
 * refusal, so if a `readOnly` row ever did reach a commit it would decline it
 * rather than send an empty patch.
 */
const NO_COMMIT = () => null;

export function DocumentAttributeGrid({
  hideEmptyRows = false,
  isGenerated = false,
  columns = 2,
  doc,
  onNotesChange,
  onCertifiedCopyChange,
  onEditingChange
}: {
  doc: DocumentItem;
  /**
   * How many cells a row holds.
   *
   * Two by default, matching the Contacts drawer at the full width of a panel.
   * The document drawer passes **one**: there the fields sit in a narrow column
   * beside the document itself, where a half-width cell is not a column, it is
   * a sliver.
   */
  columns?: 1 | 2;
  /**
   * Whether **Vale** produced this document rather than somebody sending it.
   *
   * Two rows are wrong in that case, and both for the same reason: nothing was
   * uploaded and nobody named the file but us.
   *
   * - `Uploaded` becomes **`Generated`**. The date is the same instant either
   *   way; the word is a claim about where the document came from, and on a
   *   document Vale merged from the firm's own precedent it is simply false.
   * - `Original name` is dropped. There is no original — it can only restate
   *   the title, and restates it carrying the `.docx` extension this surface
   *   deliberately does not show, directly under a `Type` row reading PDF.
   */
  isGenerated?: boolean;
  /**
   * Leave out the read-only metadata rows this document has no value for
   * (VALE-977) — see `AttributeGrid`'s own prop. Off by default, so the
   * Documents tab, where a persisted file has all of them, is unchanged.
   */
  hideEmptyRows?: boolean;
  /** Absent on a surface that cannot write — the note is then read-only. */
  onNotesChange?: (documentId: number, notes: string) => void | Promise<void>;
  onCertifiedCopyChange?: (documentId: number, isCertified: boolean) => void;
  /**
   * Whether an editor is open. The drawer needs it so `Escape` cancels the edit
   * instead of dismissing the whole sheet — Radix owns that key, and one press
   * would otherwise do both.
   */
  onEditingChange?: (editing: boolean) => void;
}) {
  /*
    Bound to the app's own mime→label map rather than a second copy of it; the
    extension is the fallback for a document stored without a mime type.

    **`getFileType` answers `'Unknown'` for an absent mime, not `null`** — a
    truthy sentinel, so the `||` below never reached the extension and every
    document without a stored mime type read `Unknown` while its own filename
    said `.pdf`. Same family as the `firstRecorded` lesson in
    `inventory-items/utils.ts`: a falsy-test chain is wrong when the first term
    reports "I don't know" as a value. Absent means absent, so it falls through
    to the filename, and a document with neither is left with nothing to say
    rather than a confident `Unknown`.
  */
  const mimeLabel = getFileType(doc.mimeType);
  const typeLabel =
    (mimeLabel && mimeLabel !== 'Unknown' ? mimeLabel : null) ??
    (doc.fileType ? doc.fileType.toUpperCase() : null);

  const fields: AttributeSpec<DocumentAttributePatch>[] = [
    {
      label: 'Type',
      display: typeLabel,
      draft: '',
      editor: { kind: 'text' },
      readOnly: true,
      toPatch: NO_COMMIT
    },
    {
      label: 'Size',
      // `0` is what the mapper produces for a file with no recorded size, and it
      // is not a size — an honest `—` beats a confident "0 Bytes".
      display: doc.sizeBytes ? formatFileSize(doc.sizeBytes) : null,
      draft: '',
      editor: { kind: 'text' },
      readOnly: true,
      toPatch: NO_COMMIT
    },
    {
      // Keyed, because the label is conditional: without a stable key the grid
      // reads the `Uploaded` → `Generated` relabel as one row leaving and
      // another arriving, and renders the departing copy over the row beneath
      // it (see `AttributeSpec.key`). The record that decides the wording
      // arrives from its own query, so the flip happens after first paint on
      // every generated document.
      key: 'uploaded-or-generated',
      label: isGenerated ? 'Generated' : 'Uploaded',
      // The long form, not the row's relative time: the row answers "recently?"
      // at a glance, and the drawer is where someone needs the actual date to
      // put in a letter.
      display: doc.uploadedAt ? formatDateLong(doc.uploadedAt) : null,
      draft: '',
      editor: { kind: 'text' },
      readOnly: true,
      toPatch: NO_COMMIT
    },
    {
      label: 'Certified copy',
      ...flagDraft(doc.isCertifiedCopy),
      editor: { kind: 'boolean' },
      toPatch: (draft) => ({ isCertifiedCopy: draft === 'yes' }),
      // Without a handler there is nothing to commit to, and a live control that
      // silently does nothing is worse than a read-out.
      readOnly: !onCertifiedCopyChange
    },
    {
      label: 'Note',
      display: doc.notes || null,
      draft: doc.notes ?? '',
      editor: { kind: 'text' },
      multiline: true,
      toPatch: (draft) => ({ notes: String(draft ?? '').trim() }),
      readOnly: !onNotesChange
    },
    {
      /*
        **The name the file arrived under, which is not the name on the row.**
        The row shows a *display* name the processors derive from the record —
        "Death Certificate - J Citizen" — and that label says nothing about what
        a bank or a client actually sent. Matching a document against an email,
        a covering letter or a firm's own file register needs the sent filename,
        and this is the only surface carrying it.

        It sits beside the note rather than on its own row, and a long
        filename therefore ellipsises. That is the accepted trade: the row is
        for recognising a name you already half-know, not for reading one, and
        a full-width cell for it pushed the note onto a line of its own and left
        half the panel empty.
      */
      label: 'Original name',
      display: doc.originalFilename || null,
      draft: '',
      editor: { kind: 'text' },
      readOnly: true,
      toPatch: NO_COMMIT
    }
  ];

  const shownFields = isGenerated
    ? fields.filter((field) => field.label !== 'Original name')
    : fields;

  return (
    <div className="mb-6">
      <AttributeGrid<DocumentAttributePatch>
        hideEmptyRows={hideEmptyRows}
        fields={shownFields}
        columns={columns}
        testId="document-attributes"
        onEditingChange={onEditingChange}
        /*
          `onSave` must not resolve until the value is in the record the row
          re-derives from (§6 / VALE-709). Both handlers await their mutation,
          whose `onSuccess` invalidates the matter-files list the drawer's
          document is read out of, so by the time this resolves the next render
          already carries the saved value. A rejection propagates, which is what
          gives the row its refusal ring instead of a silent no-op.

          One grid, one `onSave`, so it dispatches on which key the patch
          carries — `toPatch` produces exactly one per row.
        */
        onSave={async (patch) => {
          if (patch.notes !== undefined) {
            await onNotesChange?.(doc.id, patch.notes);
          }
          if (patch.isCertifiedCopy !== undefined) {
            await onCertifiedCopyChange?.(doc.id, patch.isCertifiedCopy);
          }
        }}
      />
    </div>
  );
}
